import { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import {
  KeyboardArrowDown,
  LogoutOutlined,
  ShieldOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { getUserInitials } from "../TopHeader.constants";
import {
  menuItemIconSx,
  menuItemLabelGrowSx,
  menuListSx,
  menuPaperSx,
  userInitialsButtonSx,
  userMenuOpenButtonSx,
} from "../TopHeader.style";
import {
  expanderButtonSx,
  expanderButtonTopSx,
  getChevronRotatedSx,
  nestedActionSx,
  rolesListSx,
  userEmailSx,
  userInfoBlockSx,
  userMenuWidthSx,
  userNameSx,
  userPhoneSx,
} from "./UserAccountMenu.style";
import { useHeaderMenuAnchor } from "../useHeaderMenuAnchor";

const UserAccountMenu = ({
  name = "",
  email = "",
  phone = "",
  roles = [],
  showAdministration = false,
  onLogout,
  onResetPassword,
  onAdministration,
}) => {
  const [rolesOpen, setRolesOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { anchorEl, isOpen, handleToggle, handleClose, closeMenu } =
    useHeaderMenuAnchor("account");
  const initials = getUserInitials(name) || "U";

  const handleLogout = () => {
    closeMenu();
    onLogout?.();
  };

  const handleResetPassword = () => {
    closeMenu();
    onResetPassword?.();
  };

  const handleAdministration = () => {
    closeMenu();
    onAdministration?.();
  };

  return (
    <>
      <IconButton
        onClick={handleToggle}
        aria-label={isOpen ? "Close account menu" : "Account menu"}
        title="Account"
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : undefined}
        sx={isOpen ? userMenuOpenButtonSx : userInitialsButtonSx}
      >
        {initials}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { ...menuPaperSx, ...userMenuWidthSx } },
          list: { sx: menuListSx },
        }}
      >
        <Box sx={userInfoBlockSx}>
          <Typography component="div" sx={userNameSx}>
            {name || "User"}
          </Typography>
          {email ? (
            <Typography component="div" sx={userEmailSx}>
              {email}
            </Typography>
          ) : null}
          {phone ? (
            <Typography component="div" sx={userPhoneSx}>
              {phone}
            </Typography>
          ) : null}
        </Box>

        <MenuItem
          onClick={() => setRolesOpen((prev) => !prev)}
          aria-expanded={rolesOpen ? "true" : "false"}
          sx={expanderButtonTopSx}
        >
          <ShieldOutlined sx={menuItemIconSx} />
          <Box component="span" sx={menuItemLabelGrowSx}>
            Roles · {roles.length}
          </Box>
          <KeyboardArrowDown sx={getChevronRotatedSx(rolesOpen)} />
        </MenuItem>
        {rolesOpen ? (
          <Typography component="div" sx={rolesListSx}>
            {roles.length > 0 ? roles.join(" · ") : "No roles assigned"}
          </Typography>
        ) : null}

        <MenuItem
          onClick={() => setAccountOpen((prev) => !prev)}
          aria-expanded={accountOpen ? "true" : "false"}
          sx={expanderButtonSx}
        >
          <TuneOutlined sx={menuItemIconSx} />
          <Box component="span" sx={menuItemLabelGrowSx}>
            Account settings
          </Box>
          <KeyboardArrowDown sx={getChevronRotatedSx(accountOpen)} />
        </MenuItem>
        {accountOpen ? (
          <>
            <MenuItem onClick={handleResetPassword} sx={nestedActionSx}>
              Reset password
            </MenuItem>
            {/* {showAdministration ? (
              <MenuItem onClick={handleAdministration} sx={nestedActionSx}>
                Administration
              </MenuItem>
            ) : null} */}
          </>
        ) : null}

        <MenuItem onClick={handleLogout} sx={expanderButtonTopSx}>
          <LogoutOutlined sx={menuItemIconSx} />
          Log out
        </MenuItem>
      </Menu>
    </>
  );
};

UserAccountMenu.propTypes = {
  name: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  roles: PropTypes.arrayOf(PropTypes.string),
  showAdministration: PropTypes.bool,
  onLogout: PropTypes.func,
  onResetPassword: PropTypes.func,
  onAdministration: PropTypes.func,
};

export default UserAccountMenu;
